import { useState } from "react";
import { Pin, TriangleAlert, X } from "lucide-react";
import { Trans, useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { cn } from "@jbnl/shared-ui";
import { BezettingDialog } from "./BezettingDialog";
import { CelDialog, type Verplaatsing } from "./CelDialog";
import { zonderVoorvoegsel } from "./naam";
import { PromotieMarkering } from "./PromotieMarkering";
import { datum, tijd } from "./tijd";
import {
  doel,
  waarschuwingenVoorFunctie,
  waarschuwingenVoorRegel,
  type Bezetting,
  type OngeplaatstePloeg,
  type PlanningBaan,
  type PlanningDag,
  type PlanningRegel,
  type Waarschuwing,
} from "./usePlanning";

/** What a cel or bezetting click opens: a cel's edit dialog, or the bezetting behind one cell. */
type Gekozen =
  | { soort: "cel"; baan: PlanningBaan; regel: PlanningRegel }
  | {
      soort: "bezetting";
      doelId: string;
      doel: number;
      label: string;
      bezetting: Bezetting;
    }
  | null;

/**
 * One wedstrijddag as the boekje prints it. The grid is the shape a wedstrijdleider checks a
 * season against, so it is the shape they overrule it in: every cell opens the row it stands for.
 */
export function Speelschemagrid({
  seizoenId,
  dagen,
  dag,
  ongeplaatst,
  verplaatsing,
  onVerplaatsing,
}: {
  seizoenId: string;
  dagen: PlanningDag[];
  dag: PlanningDag;
  ongeplaatst: OngeplaatstePloeg[];
  verplaatsing: Verplaatsing | null;
  onVerplaatsing: (verplaatsing: Verplaatsing | null) => void;
}) {
  const [gekozen, setGekozen] = useState<Gekozen>(null);

  return (
    <div className="space-y-6">
      {verplaatsing && (
        <VerplaatsingBanner
          verplaatsing={verplaatsing}
          onAnnuleren={() => onVerplaatsing(null)}
        />
      )}

      <SpeelschemaSectie
        dag={dag}
        verplaatsing={verplaatsing}
        onOpenCel={(baan, regel) => setGekozen({ soort: "cel", baan, regel })}
      />

      <PromotieMarkering seizoenId={seizoenId} dag={dag} />

      <BezettingSectie dag={dag} onOpen={setGekozen} />

      {dag.waarschuwingen.length > 0 && <Waarschuwingen dag={dag} />}

      <SpeelschemaDialogen
        seizoenId={seizoenId}
        dagen={dagen}
        dag={dag}
        gekozen={gekozen}
        ongeplaatst={ongeplaatst}
        verplaatsing={verplaatsing}
        onVerplaatsing={onVerplaatsing}
        setGekozen={setGekozen}
      />
    </div>
  );
}

/** The banner while a move is armed: names the ploeg being moved and offers to cancel it. */
function VerplaatsingBanner({
  verplaatsing,
  onAnnuleren,
}: {
  verplaatsing: Verplaatsing;
  onAnnuleren: () => void;
}) {
  const { t } = useTranslation("planning");

  return (
    <div className="flex flex-wrap items-center gap-3 rounded-md border border-brand-blue bg-accent px-3 py-2 text-sm">
      <span className="[&_strong]:font-medium">
        <Trans
          t={t}
          i18nKey="grid.verplaatsen"
          values={{ naam: zonderVoorvoegsel(verplaatsing.naam) }}
        />
      </span>
      <Button
        variant="ghost"
        size="sm"
        className="ms-auto"
        onClick={onAnnuleren}
      >
        <X aria-hidden />
        {t("grid.annuleren")}
      </Button>
    </div>
  );
}

/** The speelschema card: the grid itself, plus why it might have nothing to show. */
function SpeelschemaSectie({
  dag,
  verplaatsing,
  onOpenCel,
}: {
  dag: PlanningDag;
  verplaatsing: Verplaatsing | null;
  onOpenCel: (baan: PlanningBaan, regel: PlanningRegel) => void;
}) {
  const { t } = useTranslation("planning");

  // Not "the planner failed": a labelled regel never carries a ploeg either (ADR-0039), so this
  // is every placeable row on the dag standing empty.
  const geenPloegen = dag.banen.every((baan) =>
    baan.regels.every((regel) => !regel.ploeg),
  );

  return (
    <Card>
      <CardHeader>
        <CardTitle>{datum(dag.datum)}</CardTitle>
        <CardDescription>
          {dag.organiserendKorpsNaam
            ? t("grid.dagRegel", {
                niveau: dag.niveauNaam,
                korps: zonderVoorvoegsel(dag.organiserendKorpsNaam),
              })
            : dag.niveauNaam}
        </CardDescription>
      </CardHeader>
      <CardContent>
        {!dag.organiserendKorpsNaam && (
          // A dag with no host is left out of the planner's input entirely (ADR-0030), so an
          // empty grid here is not a dag nobody entered.
          <p className="mb-4 text-sm text-muted-foreground">
            {t("grid.geenKorps")}
          </p>
        )}

        {/* A dag whose banen are all empty. The grid only renders once a planning exists, so
            this is the engine having had nothing to place rather than not having run - which
            at a halve finale or finale means the promoties are not in yet (ADR-0058). */}
        {dag.organiserendKorpsNaam && dag.banen.length > 0 && geenPloegen && (
          <p className="mb-4 text-sm text-muted-foreground">
            {t("grid.geenPloegen")}
          </p>
        )}

        {dag.banen.length === 0 ? (
          <p className="text-sm text-muted-foreground">{t("grid.geenBanen")}</p>
        ) : (
          <Speelschematabel
            dag={dag}
            verplaatsing={verplaatsing}
            onOpenCel={onOpenCel}
          />
        )}
      </CardContent>
    </Card>
  );
}

/** The bezetting card: the hele-dag table and the per-baan table, whichever apply. */
function BezettingSectie({
  dag,
  onOpen,
}: {
  dag: PlanningDag;
  onOpen: OpenBezetting;
}) {
  const { t } = useTranslation("planning");

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t("bezetting.title")}</CardTitle>
        <CardDescription>{t("bezetting.description")}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {dag.dagbezetting.length > 0 && (
          <Bezettingtabel
            dag={dag}
            kop={t("bezetting.heleDag")}
            kolommen={[
              {
                id: dag.id,
                label: t("bezetting.heleDag"),
                baanId: null,
                doel: doel.dag,
                bezetting: dag.dagbezetting,
              },
            ]}
            onOpen={onOpen}
          />
        )}

        {dag.banen.length > 0 && (
          <Bezettingtabel
            dag={dag}
            kop={t("bezetting.perBaan")}
            kolommen={dag.banen.map((baan) => ({
              id: baan.id,
              label: baan.label,
              baanId: baan.id,
              doel: doel.baan,
              bezetting: baan.bezetting,
            }))}
            onOpen={onOpen}
          />
        )}
      </CardContent>
    </Card>
  );
}

/** The two dialogs a cel opens: its own edit dialog, or the bezetting behind one bezetting cel. */
function SpeelschemaDialogen({
  seizoenId,
  dagen,
  dag,
  gekozen,
  ongeplaatst,
  verplaatsing,
  onVerplaatsing,
  setGekozen,
}: {
  seizoenId: string;
  dagen: PlanningDag[];
  dag: PlanningDag;
  gekozen: Gekozen;
  ongeplaatst: OngeplaatstePloeg[];
  verplaatsing: Verplaatsing | null;
  onVerplaatsing: (verplaatsing: Verplaatsing | null) => void;
  setGekozen: (gekozen: Gekozen) => void;
}) {
  if (gekozen?.soort === "cel") {
    return (
      <CelDialog
        seizoenId={seizoenId}
        dagen={dagen}
        dag={dag}
        baan={gekozen.baan}
        regel={gekozen.regel}
        // An armed move is a landing only where the moved ploeg is not the one already
        // standing here: opening its own cell opens it, rather than dropping it on itself.
        verplaatst={
          verplaatsing !== null &&
          verplaatsing.ploegId !== gekozen.regel.ploeg?.id
            ? verplaatsing
            : null
        }
        ongeplaatst={ongeplaatst}
        onVerplaats={(ploeg) => {
          onVerplaatsing({
            ploegId: ploeg.id,
            naam: ploeg.naam,
            klasseId: gekozen.baan.klasseId,
          });
          setGekozen(null);
        }}
        onClose={() => {
          setGekozen(null);
          onVerplaatsing(null);
        }}
      />
    );
  }

  if (gekozen?.soort === "bezetting") {
    return (
      <BezettingDialog
        seizoenId={seizoenId}
        dagId={dag.id}
        doel={gekozen.doel}
        doelId={gekozen.doelId}
        doelLabel={gekozen.label}
        bezetting={gekozen.bezetting}
        onClose={() => setGekozen(null)}
      />
    );
  }

  return null;
}

/**
 * The speelschema table on its own: split out from Speelschemagrid so the header and the rows -
 * each already a few levels deep - do not stack onto the card layout around them.
 */
function Speelschematabel({
  dag,
  verplaatsing,
  onOpenCel,
}: {
  dag: PlanningDag;
  verplaatsing: Verplaatsing | null;
  onOpenCel: (baan: PlanningBaan, regel: PlanningRegel) => void;
}) {
  const { t } = useTranslation("planning");

  // Every starttijd on the dag, once, in order. The banen of one dag do not share a grid - a
  // 35-minute Junioren schedule runs beside a 30-minute Aspiranten one (ADR-0039) - so the rows
  // are the union rather than any one baan's list, and a baan with nothing at a time shows a dash.
  const starttijden = [
    ...new Set(dag.banen.flatMap((b) => b.regels.map((r) => r.starttijd))),
  ].sort();

  return (
    // Its own scroller: a dag of six banen is wider than the pane, and the page body
    // must never scroll sideways.
    <div className="overflow-x-auto">
      <table className="w-full min-w-max border-separate border-spacing-0 text-sm">
        <caption className="sr-only">
          {t("grid.caption", { datum: datum(dag.datum) })}
        </caption>
        <SpeelschemaKoprij banen={dag.banen} />
        <tbody>
          {starttijden.map((starttijd) => (
            <Speelschemarij
              key={starttijd}
              dag={dag}
              starttijd={starttijd}
              verplaatsing={verplaatsing}
              onOpenCel={onOpenCel}
            />
          ))}
        </tbody>
      </table>
    </div>
  );
}

/** The speelschema table's header row: the starttijd column plus one column per baan. */
function SpeelschemaKoprij({ banen }: { banen: PlanningBaan[] }) {
  const { t } = useTranslation("planning");

  return (
    <thead>
      <tr>
        <th scope="col" className="py-2 pe-4 text-start font-medium">
          {t("grid.start")}
        </th>
        {banen.map((baan) => (
          <th
            key={baan.id}
            scope="col"
            className="px-2 py-2 text-start font-medium"
          >
            {baan.label}
          </th>
        ))}
      </tr>
    </thead>
  );
}

/** One row of the speelschema table: every baan's cel at a single starttijd. */
function Speelschemarij({
  dag,
  starttijd,
  verplaatsing,
  onOpenCel,
}: {
  dag: PlanningDag;
  starttijd: string;
  verplaatsing: Verplaatsing | null;
  onOpenCel: (baan: PlanningBaan, regel: PlanningRegel) => void;
}) {
  const { t } = useTranslation("planning");

  return (
    <tr>
      <th
        scope="row"
        className="py-1 pe-4 text-start font-normal tabular-nums text-muted-foreground"
      >
        {tijd(starttijd)}
      </th>
      {dag.banen.map((baan) => {
        const regel = baan.regels.find((r) => r.starttijd === starttijd);

        return (
          <td key={baan.id} className="px-1 py-1">
            {regel ? (
              <Cel
                dag={dag}
                baan={baan}
                regel={regel}
                verplaatsing={verplaatsing}
                onOpen={() => onOpenCel(baan, regel)}
              />
            ) : (
              <span className="px-2 text-muted-foreground">
                {t("grid.leeg")}
              </span>
            )}
          </td>
        );
      })}
    </tr>
  );
}

// While a move is armed, the cells that can receive it are the ones to look at - a taken one
// included, because landing on it is how two ploegen trade places.
function magOntvangenVerplaatsing(
  verplaatsing: Verplaatsing | null,
  baan: PlanningBaan,
  regel: PlanningRegel,
): boolean {
  return (
    verplaatsing !== null &&
    verplaatsing.klasseId === baan.klasseId &&
    verplaatsing.ploegId !== regel.ploeg?.id
  );
}

/** A cel's button classes: highlighted where something warns, ringed where a move can land. */
function celKlasse(
  waarschuwingen: Waarschuwing[],
  verplaatsing: Verplaatsing | null,
  baan: PlanningBaan,
  regel: PlanningRegel,
): string {
  return cn(
    "flex w-full items-center gap-1.5 rounded-md px-2 py-1 text-start hover:bg-accent",
    "focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none",
    waarschuwingen.length > 0 && "bg-destructive/10 hover:bg-destructive/15",
    magOntvangenVerplaatsing(verplaatsing, baan, regel) &&
      "ring-1 ring-brand-blue",
  );
}

/** The pin on a vastgezet ploeg or bezetting: the same icon and reden tooltip in both grids. */
function VastgezetPin({ reden }: { reden: string | null }) {
  const { t } = useTranslation("planning");

  return (
    <Pin
      className="size-3.5 shrink-0 text-brand-blue"
      aria-label={
        reden ? t("grid.vastgezetMetReden", { reden }) : t("grid.vastgezet")
      }
    />
  );
}

/** The warning icon on a cell: nothing renders where there is nothing to warn about. */
function WaarschuwingBadge({
  waarschuwingen,
}: {
  waarschuwingen: Waarschuwing[];
}) {
  if (waarschuwingen.length === 0) {
    return null;
  }

  return (
    <TriangleAlert
      className="size-3.5 shrink-0 text-destructive"
      aria-label={waarschuwingen.map((w) => w.tekst).join(" ")}
    />
  );
}

/** One cell of the grid: a ploeg, a label, or a free slot. */
function Cel({
  dag,
  baan,
  regel,
  verplaatsing,
  onOpen,
}: {
  dag: PlanningDag;
  baan: PlanningBaan;
  regel: PlanningRegel;
  verplaatsing: Verplaatsing | null;
  onOpen: () => void;
}) {
  const { t } = useTranslation("planning");
  const waarschuwingen = waarschuwingenVoorRegel(dag, regel.id);

  // A labelled regel is one no ploeg may stand in (ADR-0039), so it is text rather than a
  // button: there is nothing here to open.
  if (regel.label !== null) {
    return (
      <span className="block rounded-md bg-muted px-2 py-1 italic text-muted-foreground">
        {regel.label}
      </span>
    );
  }

  return (
    <button
      type="button"
      onClick={onOpen}
      className={celKlasse(waarschuwingen, verplaatsing, baan, regel)}
    >
      {regel.ploeg ? (
        <>
          {/* Zonder voorvoegsel, per `naam.ts`: the cell is one column of a six-baan grid and
              truncates, so "Jeugdbrandweer " would eat the half that says which ploeg it is. */}
          <span className="truncate">
            {zonderVoorvoegsel(regel.ploeg.naam)}
          </span>
          {regel.ploeg.isVastgezet && (
            <VastgezetPin reden={regel.ploeg.reden} />
          )}
        </>
      ) : (
        <span className="text-muted-foreground">{t("grid.vrij")}</span>
      )}

      <WaarschuwingBadge waarschuwingen={waarschuwingen} />
    </button>
  );
}

/** One column of the bezetting table: a wedstrijdbaan, or the wedstrijddag itself. */
type Bezettingkolom = {
  id: string;
  label: string;
  baanId: string | null;
  doel: number;
  bezetting: Bezetting[];
};

/** Opens the dialog for one bezetting cell, named after the doel it edits. */
type OpenBezetting = (keuze: {
  soort: "bezetting";
  doelId: string;
  doel: number;
  label: string;
  bezetting: Bezetting;
}) => void;

/**
 * A matrix, because the two questions are read down a column and along a row. The rows union what
 * every column needs, keeping "niet nodig" apart from "required and empty" (ADR-0021).
 */
function Bezettingtabel({
  dag,
  kop,
  kolommen,
  onOpen,
}: {
  dag: PlanningDag;
  kop: string;
  kolommen: Bezettingkolom[];
  onOpen: OpenBezetting;
}) {
  // The stamgegevens order the server carries on every row, not alphabetical: the
  // wedstrijdboekjes list a baan's jobs in the order they are performed, and that is what a
  // wedstrijdleider reads down. The rows are the union over every column, so the sort has to
  // happen after the merge rather than being inherited from any one baan's list.
  const functies = [
    ...new Map(
      kolommen
        .flatMap((k) => k.bezetting)
        .map((b) => [b.functieId, b] as const),
    ).values(),
  ].sort(
    (a, b) =>
      // Number() because openapi-typescript widens every int32 in this schema to
      // `number | string`. Everywhere else the server hands lists over already ordered and
      // nothing client-side does arithmetic on one; this table is the exception, because its
      // rows are a union over several banen and the merge has to re-sort them.
      Number(a.volgorde) - Number(b.volgorde) ||
      a.functieNaam.localeCompare(b.functieNaam, "nl"),
  );

  if (functies.length === 0) {
    return null;
  }

  return (
    <div>
      <h3 className="mb-2 text-sm font-medium">{kop}</h3>
      <Bezettingmatrix
        dag={dag}
        kop={kop}
        kolommen={kolommen}
        functies={functies}
        onOpen={onOpen}
      />
    </div>
  );
}

/**
 * The bezetting table itself: split out from Bezettingtabel so the header and the rows - each
 * already a few levels deep - do not stack onto the heading above them.
 */
function Bezettingmatrix({
  dag,
  kop,
  kolommen,
  functies,
  onOpen,
}: {
  dag: PlanningDag;
  kop: string;
  kolommen: Bezettingkolom[];
  functies: Bezetting[];
  onOpen: OpenBezetting;
}) {
  const { t } = useTranslation("planning");

  return (
    // Its own scroller, like the speelschema above it: six banen is wider than the pane.
    <div className="overflow-x-auto">
      <table className="w-full min-w-max border-separate border-spacing-0 text-sm">
        <caption className="sr-only">
          {t("bezetting.caption", { kop, datum: datum(dag.datum) })}
        </caption>
        <BezettingKoprij kolommen={kolommen} />
        <tbody>
          {functies.map((functie) => (
            <BezettingRij
              key={functie.functieId}
              dag={dag}
              functie={functie}
              kolommen={kolommen}
              onOpen={onOpen}
            />
          ))}
        </tbody>
      </table>
    </div>
  );
}

/** The bezetting table's header row: the functie column plus one column per kolom. */
function BezettingKoprij({ kolommen }: { kolommen: Bezettingkolom[] }) {
  const { t } = useTranslation("planning");

  return (
    <thead>
      <tr>
        <th scope="col" className="py-2 pe-4 text-start font-medium">
          {t("bezetting.functie")}
        </th>
        {kolommen.map((kolom) => (
          <th
            key={kolom.id}
            scope="col"
            className="px-2 py-2 text-start font-medium"
          >
            {kolom.label}
          </th>
        ))}
      </tr>
    </thead>
  );
}

/** One row of the bezetting table: one functie across every kolom. */
function BezettingRij({
  dag,
  functie,
  kolommen,
  onOpen,
}: {
  dag: PlanningDag;
  functie: Bezetting;
  kolommen: Bezettingkolom[];
  onOpen: OpenBezetting;
}) {
  const { t } = useTranslation("planning");

  return (
    <tr>
      <th
        scope="row"
        className="py-1 pe-4 text-start font-normal text-muted-foreground"
      >
        {functie.functieNaam}
      </th>
      {kolommen.map((kolom) => {
        const bezetting = kolom.bezetting.find(
          (b) => b.functieId === functie.functieId,
        );

        return (
          <td key={kolom.id} className="px-1 py-1">
            {bezetting ? (
              <Bezettingcel
                dag={dag}
                kolom={kolom}
                bezetting={bezetting}
                onOpen={() =>
                  onOpen({
                    soort: "bezetting",
                    doel: kolom.doel,
                    doelId: kolom.id,
                    label: t("bezetting.doelLabel", {
                      datum: datum(dag.datum),
                      kolom: kolom.label,
                    }),
                    bezetting,
                  })
                }
              />
            ) : (
              // Not required here, which is not the same as required and empty.
              <span className="px-2 text-muted-foreground/60">
                {t("bezetting.nietNodig")}
              </span>
            )}
          </td>
        );
      })}
    </tr>
  );
}

/** One cell of the bezetting table: who is in this functie on this baan, if anybody. */
function Bezettingcel({
  dag,
  kolom,
  bezetting,
  onOpen,
}: {
  dag: PlanningDag;
  kolom: Bezettingkolom;
  bezetting: Bezetting;
  onOpen: () => void;
}) {
  const { t } = useTranslation("planning");
  const waarschuwingen = waarschuwingenVoorFunctie(
    dag,
    kolom.baanId,
    bezetting.functieId,
  );

  return (
    <button
      type="button"
      onClick={onOpen}
      className={cn(
        "flex w-full items-center gap-1.5 rounded-md px-2 py-1 text-start hover:bg-accent",
        "focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none",
        waarschuwingen.length > 0 &&
          "bg-destructive/10 hover:bg-destructive/15",
      )}
    >
      <span
        className={cn(
          "truncate",
          !bezetting.persoonNaam && "text-muted-foreground",
        )}
      >
        {bezetting.persoonNaam ?? t("bezetting.nietBezet")}
      </span>
      {bezetting.isVastgezet && <VastgezetPin reden={bezetting.reden} />}
      <WaarschuwingBadge waarschuwingen={waarschuwingen} />
    </button>
  );
}

/**
 * Written out, not left on the cells: a warning that exists only as an icon is one nobody on a
 * keyboard or a phone reads, and ADR-0038 asks for one people encounter. Never a refusal - #72
 * is explicit that a pin making the rest unsatisfiable is normal.
 */
function Waarschuwingen({ dag }: { dag: PlanningDag }) {
  const { t } = useTranslation("planning");

  return (
    // The same red field as the ongeplaatste ploegen, and for the same reason: this is the list
    // of what is wrong with the dag, and it was reading as a footnote under the grid.
    <Card className="border-destructive-border bg-destructive-surface">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <TriangleAlert className="size-4 text-destructive" aria-hidden />
          {t("waarschuwingen.title")}
        </CardTitle>
        <CardDescription className="text-destructive-muted">
          {t("waarschuwingen.description")}
        </CardDescription>
      </CardHeader>
      <CardContent>
        {/* Numbered, because a number is what makes one referable over the phone. */}
        <ol className="list-decimal space-y-1 ps-5 text-sm marker:text-destructive-muted">
          {dag.waarschuwingen.map((waarschuwing: Waarschuwing, index) => (
            // Where it is, in front of what is wrong (#121). The server's sentences name a
            // ploeg, a persoon or a functie and never a baan - unambiguous on the cell that
            // carries the badge, useless in a list under a dag that runs four of them.
            <li key={`${waarschuwing.code}-${index}`}>
              {t("waarschuwingen.regel", {
                baan: waarschuwing.baanLabel ?? t("waarschuwingen.heleDag"),
                tekst: waarschuwing.tekst,
              })}
            </li>
          ))}
        </ol>
      </CardContent>
    </Card>
  );
}
